import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Filter, Folder } from 'lucide-react'
import { categoryService } from '../services/api'
import CategoryCard from '../components/CategoryCard'
import LoadingSpinner from '../components/LoadingSpinner'

const Categories = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    sort: 'sort_order',
    order: 'asc'
  })

  // Update filters when URL search parameters change
  useEffect(() => {
    const search = searchParams.get('search') || ''
    
    setFilters(prev => ({
      ...prev,
      search
    }))
  }, [searchParams])

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories', true], // Include products count
    queryFn: () => categoryService.getCategories(true),
    keepPreviousData: true,
  })

  useEffect(() => {
    const newSearchParams = new URLSearchParams()
    if (filters.search) {
      newSearchParams.set('search', filters.search)
    }
    setSearchParams(newSearchParams)
  }, [filters, setSearchParams])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }))
  }

  // Filter and sort categories
  const processedCategories = React.useMemo(() => {
    let categories = categoriesData?.categories || []
    
    // Filter by search
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      categories = categories.filter(category => 
        category.name.toLowerCase().includes(searchTerm) ||
        (category.description && category.description.toLowerCase().includes(searchTerm))
      )
    }
    
    // Sort categories
    categories = [...categories].sort((a, b) => {
      let aVal, bVal
      
      switch (filters.sort) {
        case 'name':
          aVal = a.name.toLowerCase()
          bVal = b.name.toLowerCase()
          break
        case 'created_at':
          aVal = new Date(a.created_at)
          bVal = new Date(b.created_at)
          break
        case 'product_count':
          aVal = a.product_count || 0
          bVal = b.product_count || 0
          break
        case 'sort_order':
          aVal = a.sort_order || 999999
          bVal = b.sort_order || 999999
          break
        default:
          aVal = a.name.toLowerCase()
          bVal = b.name.toLowerCase()
      }
      
      if (filters.order === 'desc') {
        return aVal < bVal ? 1 : -1
      }
      return aVal > bVal ? 1 : -1
    })
    
    return categories
  }, [categoriesData, filters])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4 flex items-center">
          <Folder className="h-8 w-8 mr-3 text-primary-600" />
          الفئات
        </h1>
        <p className="text-gray-600">
          تصفح جميع الفئات واكتشف منتجاتنا المتنوعة
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Filters */}
        <div className="lg:w-64 space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Filter className="h-5 w-5 mr-2" />
              المرشحات
            </h3>

            {/* Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                البحث
              </label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="البحث في الفئات..."
                className="input"
              />
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ترتيب حسب
              </label>
              <select
                value={`${filters.sort}-${filters.order}`}
                onChange={(e) => {
                  const [sort, order] = e.target.value.split('-')
                  handleFilterChange('sort', sort)
                  handleFilterChange('order', order)
                }}
                className="input"
              >
                <option value="sort_order-asc">ترتيب افتراضي</option>
                <option value="name-asc">الاسم أ-ي</option>
                <option value="name-desc">الاسم ي-أ</option>
                <option value="product_count-desc">الأكثر منتجات</option>
                <option value="product_count-asc">الأقل منتجات</option>
                <option value="created_at-desc">الأحدث أولاً</option>
                <option value="created_at-asc">الأقدم أولاً</option>
              </select>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="text-sm text-gray-500">
              {processedCategories.length > 0 ? (
                `عرض ${processedCategories.length} فئة`
              ) : (
                'لا توجد فئات'
              )}
            </div>
          </div>

          {/* Categories Grid */}
          {categoriesLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="xl" />
            </div>
          ) : (
            <>
              {processedCategories.length > 0 ? (
                <div className="cards">
                  {processedCategories.map((category) => (
                    <CategoryCard key={category.id} category={category} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Folder className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-muted text-lg">لم يتم العثور على فئات</p>
                  <p className="text-subtle">جرب تعديل المرشحات</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Categories
